/**
 * Generates a full MUI-compatible color palette from a single hex color.
 * Uses HSL manipulation to derive light/dark variants and a dark-mode main.
 */

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(color * 255)
      .toString(16)
      .padStart(2, '0');
  };

  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/**
 * Convert hex to space-separated RGB channel string for CSS variables.
 * e.g. "#FF0000" -> "255 0 0"
 */
export function hexToRgbChannel(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

/**
 * WCAG relative luminance check - returns '#FFFFFF' or '#000000'.
 */
function contrastText(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const lum =
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  return lum > 0.179 ? '#000000' : '#FFFFFF';
}

/**
 * Generate a full palette from a single hex color.
 *
 * @param {string} hex - 7-char hex color, e.g. "#E63946"
 * @returns {{ main, light, dark, contrastText, mainChannel, lightChannel, darkChannel, darkMode }}
 */
export function generateBrandPalette(hex) {
  const normalized = hex.toUpperCase();
  const [h, s, l] = hexToHsl(normalized);

  const main = normalized;
  const light = hslToHex(h, Math.max(s - 5, 0), Math.min(l + 15, 90));
  const dark = hslToHex(h, Math.min(s + 5, 100), Math.max(l - 15, 10));

  // Dark mode: brighter version for contrast on dark backgrounds
  const darkModeMain = hslToHex(h, Math.max(s - 10, 0), Math.min(l + 25, 85));
  const darkModeLight = hslToHex(
    h,
    Math.max(s - 15, 0),
    Math.min(l + 35, 92),
  );
  const darkModeDark = normalized;

  return {
    main,
    light,
    dark,
    contrastText: contrastText(main),
    mainChannel: hexToRgbChannel(main),
    lightChannel: hexToRgbChannel(light),
    darkChannel: hexToRgbChannel(dark),
    darkMode: {
      main: darkModeMain,
      light: darkModeLight,
      dark: darkModeDark,
      contrastText: contrastText(darkModeMain),
      mainChannel: hexToRgbChannel(darkModeMain),
      lightChannel: hexToRgbChannel(darkModeLight),
      darkChannel: hexToRgbChannel(darkModeDark),
    },
  };
}
